/**
 * Murphy Door Configurator - Validation Manager (Support Module)
 *
 * PURPOSE: Manages validation rules for selections, state transitions, forbidden
 * combinations, cart items, image layers, and initialization data integrity.
 *
 * PHASE: 2 (Event-Driven CCP - Strict Enforcement)
 * MODULE TYPE: Support (support-)
 * GOLDEN STANDARD REFERENCE: md-config-bl-material-selector.js (200/200)
 *
 * CCP COMPLIANCE:
 * - CONSTANTS.EVENTS.* exclusively (no local EVENT_TYPES - Pattern 19)
 * - publishEvent() async helper with try/catch (Pattern 2, 11)
 * - 5-stage event lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED (Pattern 3)
 * - Event consumer handler for bootstrap (Pattern 4)
 * - Bootstrap runtime protection via SYSTEM_STATE_FROM_PPD_READY (Pattern 6)
 * - PPD-first O(1) data access via conductor delegation (Patterns 7, 8)
 * - Data access through conductor delegation (Pattern 9)
 * - UI update events for validation feedback (Pattern 10)
 * - Performance tracking with thresholds (Patterns 12, 13)
 * - Try/catch + FAILED events on all operations (Pattern 14)
 * - Cleanup arrays for all subscriptions (Pattern 15)
 * - Initialization guard (Pattern 16)
 * - XSS prevention - no user input rendered to DOM (Pattern 17 - N/A for pure support)
 * - CoreEventBus central freeze (Pattern 18 DRY)
 * - Conductor single publication (Pattern 20)
 *
 * PERFORMANCE CHARACTERISTICS:
 * - Tab validation: O(1) Map.has() lookup
 * - Category validation: O(1) Set.has() lookup
 * - Variant validation: O(1) Map.has() dual-lookup
 * - Data structure validation: O(1) pre-built PPD metadata
 *
 * All external communication flows through md-config-core-conductor.js conductor.
 *
 * Dependencies:
 * - md-config-core-constants.js (event constants)
 * - md-config-core-event-bus.js (event system)
 * - md-config-core-conductor.js (PPD delegation, error handling)
 *
 * This module is used by:
 * - md-config-core-conductor.js (primary conductor)
 */
(function (window) {
  'use strict';

  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 1 & 19: GLOBAL CONSTANTS USAGE (10 points each)
  // ALL events use CONSTANTS.EVENTS.* - NO local EVENT_TYPES (DRY)
  // ============================================================================
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;
  const EVENTS = CONSTANTS.EVENTS;
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    STATE_CHANGE_VALIDATION: 10, // ms - O(1) lookups should be fast
    INIT_VALIDATION: 15, // ms - PPD metadata checks
  });

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
  // For validation modules: methods return safe failure (not queued) before bootstrap
  // ============================================================================
  let bootstrapComplete = false;

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  const cleanupHandlers = [];

  // ============================================================================
  // PATTERN 2: publishEvent() ASYNC HELPER (10 points)
  // PATTERN 11: ASYNC EVENT PUBLISHING (10 points)
  // NOTE: Object.freeze() NOT needed - CoreEventBus.publish() freezes centrally (Pattern 18 DRY)
  // ============================================================================
  async function publishEvent(eventType, eventData) {
    try {
      if (CoreEventBus?.publish) {
        await CoreEventBus.publish(eventType, eventData);
      } else {
        isDev() &&
          console.warn(
            `[ValidationManager] Event bus unavailable, failed to publish: ${eventType}`
          );
      }
    } catch (error) {
      isDev() &&
        console.error(
          `[ValidationManager] Error publishing ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(EVENTS.PERFORMANCE_VALIDATION_SLOW, {
        operation: operationType,
        duration,
        threshold,
        severity: duration > threshold * 2 ? 'critical' : 'warning',
        timestamp: Date.now(),
      });
    }
  }

  // ============================================================================
  // PATTERN 9: DATA REQUEST VIA CONDUCTOR DELEGATION (10 points)
  // All PPD access goes through conductor methods
  // ============================================================================
  function getPPD() {
    return (
      window.MurphyDoorConfigurator.main?.getPerfectProductDictionary?.() ||
      null
    );
  }

  function getCategoryStructure() {
    return window.MurphyDoorConfigurator.main?.getCategoryStructure?.() || null;
  }

  function getProductStructure() {
    return window.MurphyDoorConfigurator.main?.getProductStructure?.() || null;
  }

  // ============================================================================
  // PATTERN 4: EVENT CONSUMER HANDLERS (10 points)
  // PATTERN 6: BOOTSTRAP SUBSCRIPTION AT MODULE LOAD TIME (10 points)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log('[ValidationManager] Bootstrap complete event received');
        bootstrapComplete = true;
        isInitialized = true;

        // PATTERN 5: Event Replay Ready - publish module ready event
        publishEvent(EVENTS.MODULE_VALIDATION_MANAGER_READY, {
          module: 'validationManager',
          timestamp: Date.now(),
        });
      }
    );
    cleanupHandlers.push(unsubBootstrap);

    isDev() &&
      console.log(
        '[ValidationManager] Subscribed to bootstrap event at module load time'
      );
  }

  /**
   * Lazy-load DOM from uiUpdater to avoid initialization race condition.
   * uiUpdater loads after validation-manager, so we can't access it at module load time.
   * @returns {Object|null} DOM object from uiUpdater, or null if not available
   */
  function getDOM() {
    if (!window.MurphyDoorConfigurator?.uiUpdater?.DOM) {
      isDev() &&
        console.error('[ValidationManager] uiUpdater.DOM not available yet');
      return null;
    }
    return window.MurphyDoorConfigurator.uiUpdater.DOM;
  }

  // ============================================================================
  // MODULE IMPLEMENTATION
  // ============================================================================
  window.MurphyDoorConfigurator.supportValidationManager = {
    // ========================================================================
    // INITIALIZATION VALIDATION
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
    // PATTERN 14: TRY/CATCH + FAILED EVENTS (10 points)
    // ========================================================================

    /**
     * Validates the configurator initialization data to ensure all required
     * components are present and properly structured for system operation.
     *
     * Implements 5-stage lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {Object} data - The processed configurator data
     * @param {Map} data.tabStructure - Map of tab configurations
     * @param {Map} data.categoryStructure - Map of category configurations
     * @param {Object} data.productStructure - Product configurations
     * @returns {Object} Validation result with detailed status information
     */
    validateInitialization(data) {
      const startTime = performance.now();

      try {
        // STAGE 1: START
        publishEvent(EVENTS.SUPPORT_VALIDATION_INIT_STARTED, {
          hasData: !!data,
          timestamp: Date.now(),
        });

        // STAGE 2: VALIDATION (input validation)
        const hasRequiredData = this.validateRequiredData(data);
        publishEvent(EVENTS.SUPPORT_VALIDATION_INIT_VALIDATION, {
          hasRequiredData,
          timestamp: Date.now(),
        });

        // STAGE 3: PROGRESS (structure validation)
        let hasValidStructure = false;
        if (hasRequiredData) {
          hasValidStructure = this.validateDataStructures(data);
        }

        const result = {
          hasRequiredData,
          hasValidStructure,
          details: {
            tabStructureValid:
              data && data.tabStructure && data.tabStructure.size > 0,
            categoryStructureValid:
              data && data.categoryStructure && data.categoryStructure.size > 0,
            productStructureValid:
              data &&
              data.productStructure &&
              data.productStructure.addOnProducts instanceof Map,
          },
        };

        // STAGE 4: COMPLETED
        const duration = performance.now() - startTime;
        publishEvent(EVENTS.SUPPORT_VALIDATION_INIT_COMPLETED, {
          result,
          duration,
          timestamp: Date.now(),
        });

        // PATTERN 12: Performance tracking
        trackPerformance('INIT_VALIDATION', duration);

        return result;
      } catch (error) {
        // STAGE 5: FAILED
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_VALIDATION_INIT_FAILED, {
          error: error.message,
          duration,
          timestamp: Date.now(),
        });

        // PATTERN 14: Structured error handling via conductor
        window.MurphyDoorConfigurator.main.handleError(
          'Validation initialization failed',
          error,
          {
            methodSignature: 'validationManager.validateInitialization',
            module: 'validationManager',
            severity: 'critical',
            dataKeys: data ? Object.keys(data) : [],
          }
        );

        return {
          hasRequiredData: false,
          hasValidStructure: false,
          error: error.message,
          errorStack: error.stack,
        };
      }
    },

    /**
     * Validates that all required data is present in the configuration data.
     *
     * @param {Object} data - The processed configurator data
     * @returns {boolean} Whether all required data is present
     */
    validateRequiredData(data) {
      if (!data) return false;

      return Boolean(
        data.tabStructure &&
          data.tabStructure instanceof Map &&
          data.tabStructure.size > 0 &&
          data.categoryStructure &&
          data.categoryStructure instanceof Map &&
          data.categoryStructure.size > 0 &&
          data.productStructure &&
          data.productStructure.addOnProducts instanceof Map
      );
    },

    /**
     * Validates the structure of the configuration data using PPD pre-built metadata.
     * PATTERN 7: Perfect Dictionary Only - O(1) pre-validated schema checks
     *
     * @param {Object} data - The processed configurator data
     * @returns {boolean} Whether the data structure is valid
     */
    validateDataStructures(data) {
      try {
        // PATTERN 6: Bootstrap barrier - fail-safe if not ready
        if (!bootstrapComplete) {
          isDev() &&
            console.warn(
              '[ValidationManager] Bootstrap not complete, validateDataStructures() returning false'
            );
          return false;
        }

        // PATTERN 7 & 9: PPD-First via conductor delegation
        const ppd = getPPD();

        if (!ppd || !ppd.validationMetadata) {
          isDev() &&
            console.error(
              '[ValidationManager] validationMetadata not available in PPD'
            );
          return false;
        }

        // O(1) validation using pre-built Sets and schema validation
        const tabsValid = ppd.validationMetadata.validTabIds.size > 0;
        const categoriesValid =
          ppd.validationMetadata.validCategoryIds.size > 0;
        const productsValid = ppd.products && ppd.products.size > 0;

        // Check for schema errors detected during PPD build
        if (ppd.validationMetadata.hasSchemaErrors) {
          return false;
        }

        return tabsValid && categoriesValid && productsValid;
      } catch (error) {
        // PATTERN 14: Failed event + structured error
        publishEvent(EVENTS.SUPPORT_VALIDATION_INIT_FAILED, {
          error: error.message,
          stage: 'dataStructures',
          timestamp: Date.now(),
        });

        window.MurphyDoorConfigurator.main.handleError(
          'Data structure validation failed',
          error,
          {
            methodSignature: 'validationManager.validateDataStructures',
            module: 'validationManager',
            severity: 'critical',
          }
        );

        return false;
      }
    },

    // ========================================================================
    // STATE CHANGE VALIDATION
    // PATTERN 3: 5-STAGE EVENT LIFECYCLE (10 points)
    // ========================================================================

    /**
     * Validates state changes based on action type, ensuring data integrity
     * before allowing state transitions.
     *
     * Implements 5-stage lifecycle: STARTED -> VALIDATION -> PROGRESS -> COMPLETED/FAILED
     *
     * @param {string} action - The action type to validate
     * @param {Object} data - The data associated with the action
     * @returns {boolean} True if the state change is valid, false otherwise
     */
    validateStateChange(action, data) {
      const startTime = performance.now();

      // STAGE 1: START
      publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_STARTED, {
        action,
        timestamp: Date.now(),
      });

      if (!action) {
        // STAGE 5: FAILED (no action)
        publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_FAILED, {
          error: 'Action parameter is required for validation',
          timestamp: Date.now(),
        });

        window.MurphyDoorConfigurator.main.handleError(
          'Invalid validation request',
          new Error('Action parameter is required for validation'),
          {
            method: 'validationManager.validateStateChange',
            severity: 'warning',
            recoverable: true,
          }
        );
        return false;
      }

      try {
        // STAGE 2: VALIDATION (input check)
        if (!data) {
          publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_VALIDATION, {
            action,
            valid: false,
            reason: 'missing_data',
            timestamp: Date.now(),
          });

          window.MurphyDoorConfigurator.main.handleError(
            'Missing data for validation',
            new Error(`Data object required for action: ${action}`),
            {
              method: 'validationManager.validateStateChange',
              action,
              severity: 'warning',
              recoverable: true,
            }
          );
          return false;
        }

        publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_VALIDATION, {
          action,
          valid: true,
          timestamp: Date.now(),
        });

        // STAGE 3: PROGRESS (action-specific validation)
        publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_PROGRESS, {
          action,
          stage: 'dispatching',
          timestamp: Date.now(),
        });

        let isValid = true;

        // Process validation based on action type
        switch (action) {
          case 'TAB_CHANGED':
            isValid = this.validateTabChange(data.tabId);
            break;
          case 'CATEGORY_TOGGLE':
            isValid = this.validateCategoryToggle(data.categoryId);
            break;
          case 'MULTI_VARIANT_START':
            isValid = this.validateMultiVariantStart(data.productId);
            break;
          case 'BACK_CLICKED':
            isValid = this.validateBackNavigation(data.categoryId);
            break;
          case 'INIT_IMAGE_LAYERS':
            isValid = this.validateImageLayerInit(data);
            break;
          case 'UPDATE_IMAGE_LAYER':
            if (!data.categoryId) {
              window.MurphyDoorConfigurator.main.handleError(
                'Missing category ID for layer update',
                new Error(
                  'Category ID is required for UPDATE_IMAGE_LAYER action'
                ),
                {
                  method: 'validationManager.validateStateChange',
                  action,
                  severity: 'warning',
                  recoverable: true,
                }
              );
              isValid = false;
            } else {
              isValid = data.layerData
                ? this.validateImageLayerData(data.layerData)
                : false;
            }
            break;
          case 'SHOW_IMAGE_LAYERS':
            isValid =
              data.frontView instanceof HTMLElement &&
              data.backView instanceof HTMLElement &&
              window.MurphyDoorConfigurator.DOM.hasClass(
                data.frontView,
                'murphy-door-configurator__front-view'
              ) &&
              window.MurphyDoorConfigurator.DOM.hasClass(
                data.backView,
                'murphy-door-configurator__back-view'
              );
            break;
          case 'DIRECT_VARIANT':
            if (!data.categoryId || !data.variantId) {
              window.MurphyDoorConfigurator.main.handleError(
                'Missing required data for variant validation',
                new Error(
                  'Both categoryId and variantId are required for DIRECT_VARIANT action'
                ),
                {
                  method: 'validationManager.validateStateChange',
                  action,
                  dataKeys: Object.keys(data),
                  severity: 'warning',
                  recoverable: true,
                }
              );
              isValid = false;
            } else {
              isValid = this.validateVariantForMaterials(
                data.categoryId,
                data.variantId
              );
            }
            break;
          default:
            // Unknown action types pass through
            isValid = true;
            break;
        }

        // STAGE 4: COMPLETED
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_COMPLETED, {
          action,
          isValid,
          duration,
          timestamp: Date.now(),
        });

        // PATTERN 12: Performance tracking
        trackPerformance('STATE_CHANGE_VALIDATION', duration);

        return isValid;
      } catch (error) {
        // STAGE 5: FAILED
        const duration = performance.now() - startTime;

        publishEvent(EVENTS.SUPPORT_VALIDATION_STATE_CHANGE_FAILED, {
          action,
          error: error.message,
          duration,
          timestamp: Date.now(),
        });

        window.MurphyDoorConfigurator.main.handleError(
          `Validation error for action: ${action}`,
          error,
          {
            method: 'validationManager.validateStateChange',
            action,
            dataKeys: Object.keys(data),
            severity: 'error',
            recoverable: true,
          }
        );

        // PATTERN 10: UI Update Events (validation error feedback)
        publishEvent(EVENTS.VALIDATION_ERROR, {
          action,
          error: error.message,
          timestamp: Date.now(),
        });

        return false;
      }
    },

    // ========================================================================
    // TAB & CATEGORY VALIDATION (PPD-First O(1) lookups)
    // PATTERN 7: Perfect Dictionary Only (10 points)
    // PATTERN 8: No Raw Data Access (10 points)
    // ========================================================================

    /**
     * Validates tab change operations against the PPD tab structure.
     * O(1) Map.has() lookup - supports numeric IDs and Shopify GIDs.
     *
     * @param {string|number} tabId - The ID of the tab being changed to
     * @returns {boolean} True if the tab is valid, false otherwise
     */
    validateTabChange(tabId) {
      try {
        // Special case for summary tab which is always valid
        if (tabId === 'summary') {
          return true;
        }

        // PATTERN 6: Bootstrap barrier
        if (!bootstrapComplete) {
          isDev() &&
            console.warn(
              '[ValidationManager] Bootstrap not complete, validateTabChange() returning false'
            );
          return false;
        }

        // PATTERN 7 & 9: PPD-First via conductor delegation
        const ppd = getPPD();

        if (!ppd || !ppd.tabStructure) {
          window.MurphyDoorConfigurator.main.handleError(
            'PPD tabStructure not available',
            new Error('Cannot validate tab without PPD tabStructure'),
            {
              method: 'validationManager.validateTabChange',
              tabId,
              severity: 'error',
              recoverable: false,
            }
          );
          return false;
        }

        // O(1) Map.has() lookup - works for both numeric IDs and Shopify GIDs
        const isValid = ppd.tabStructure.has(tabId);

        if (!isValid) {
          const availableTabs = ppd.tabMetadata.allTabIds.join(', ');

          window.MurphyDoorConfigurator.main.handleError(
            'Invalid tab selection',
            new Error(
              `Tab ${tabId} not found in structure. Available tabs: ${availableTabs}`
            ),
            {
              method: 'validationManager.validateTabChange',
              tabId,
              availableTabs,
              severity: 'warning',
              recoverable: true,
            }
          );

          // PATTERN 10: UI feedback event for validation failure
          publishEvent(EVENTS.VALIDATION_TAB_CHANGE_FAILED, {
            tabId,
            availableTabs,
            timestamp: Date.now(),
          });
        }

        return isValid;
      } catch (error) {
        window.MurphyDoorConfigurator.main.handleError(
          'Error validating tab change',
          error,
          {
            method: 'validationManager.validateTabChange',
            tabId,
            severity: 'error',
            recoverable: true,
          }
        );

        return false;
      }
    },

    /**
     * Validates category toggle against PPD validationMetadata.
     * O(1) Set.has() lookup.
     *
     * @param {string} categoryId - Category ID to validate
     * @returns {boolean} True if category exists
     */
    validateCategoryToggle(categoryId) {
      // PATTERN 6: Bootstrap barrier
      if (!bootstrapComplete) {
        isDev() &&
          console.warn(
            '[ValidationManager] Bootstrap not complete, validateCategoryToggle() returning false'
          );
        return false;
      }

      // PATTERN 7 & 9: PPD-First via conductor delegation
      const ppd = getPPD();

      if (!ppd || !ppd.validationMetadata) {
        window.MurphyDoorConfigurator.main.handleError(
          'PPD validationMetadata not available',
          new Error('Cannot validate category without PPD validationMetadata'),
          {
            method: 'validationManager.validateCategoryToggle',
            categoryId,
            severity: 'error',
            recoverable: false,
          }
        );
        return false;
      }

      // O(1) Set.has() lookup
      const isValid = ppd.validationMetadata.validCategoryIds.has(categoryId);

      if (!isValid) {
        const availableCategories = Array.from(
          ppd.validationMetadata.validCategoryIds
        ).join(', ');
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid category selection',
          new Error(
            `Category ${categoryId} not found in structure. Available: ${availableCategories}`
          )
        );
      }
      return isValid;
    },

    // ========================================================================
    // SELECTION VALIDATION
    // ========================================================================

    /**
     * Validates selection state changes (browsing vs apply).
     *
     * @param {string} action - Selection action type
     * @param {Object} data - Selection data
     * @returns {boolean} True if selection is valid
     */
    validateSelectionState(action, data) {
      switch (action) {
        case 'SELECTION_BROWSING':
          return this.validateBrowsingSelection(data);
        case 'APPLY_SELECTION':
          return this.validateApplySelection(data);
        default:
          return true;
      }
    },

    /**
     * Validates apply selection data via PPD + conductor delegation.
     * PATTERN 7: PPD-first for variant existence checks.
     *
     * @param {Object} data - Selection data with categoryId, productId, variantId
     * @returns {boolean} True if selection can be applied
     */
    validateApplySelection(data) {
      const { categoryId, productId, variantId } = data;

      // PATTERN 9: Category validation via conductor delegation
      const catStructure = getCategoryStructure();
      if (!categoryId || !catStructure || !catStructure.has(categoryId)) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid selection',
          new Error(`Category ${categoryId} not found in structure`)
        );
        return false;
      }

      // PATTERN 9: Product validation via conductor delegation
      const prodStructure = getProductStructure();
      if (!prodStructure) return false;

      const product = prodStructure.addOnProducts.get(parseInt(productId));

      if (!product) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid selection',
          new Error(`Product ${productId} not found in structure`)
        );
        return false;
      }

      // Validate variant for multi-variant products
      if (product.type === 'multi-variant' && !variantId) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid selection',
          new Error('Variant selection required for multi-variant product')
        );
        return false;
      }

      // PATTERN 7: PPD-first variant existence check (O(1))
      if (variantId) {
        const ppd = getPPD();
        if (!this._variantExistsInPPD(ppd, variantId)) {
          window.MurphyDoorConfigurator.main.handleError(
            'Invalid selection',
            new Error(`Variant ${variantId} not found for product ${productId}`)
          );
          return false;
        }
      }

      return true;
    },

    /**
     * Validates browsing selection data.
     *
     * @param {Object} data - Browsing data with productId
     * @returns {boolean} True if browsing selection is valid
     */
    validateBrowsingSelection(data) {
      const { productId } = data;
      if (!productId) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid selection',
          new Error('Product ID is required')
        );
        return false;
      }
      return true;
    },

    // ========================================================================
    // TRANSITION VALIDATION
    // ========================================================================

    /**
     * Validates multi-variant start transition.
     * Supports CompositeProduct string IDs.
     *
     * @param {string|number} productId - Product ID to validate
     * @returns {boolean} True if multi-variant start is valid
     */
    validateMultiVariantStart(productId) {
      // CompositeProducts are virtual multi-variant products by design - always valid
      if (typeof productId === 'string' && productId.startsWith('composite-')) {
        return true;
      }

      // PATTERN 9: Conductor delegation for product structure
      const prodStructure = getProductStructure();
      if (!prodStructure) return true;

      const product = prodStructure.addOnProducts.get(parseInt(productId));

      // Allow state change to proceed if product not found
      if (!product) {
        return true;
      }

      // Use dynamic classification instead of hardcoded type
      return !window.MurphyDoorConfigurator.main.isSingleVariant(product);
    },

    /**
     * Validates back navigation via conductor delegation.
     *
     * @param {string} categoryId - Category to navigate back to
     * @returns {boolean} True if back navigation is valid
     */
    validateBackNavigation(categoryId) {
      // PATTERN 9: Conductor delegation
      const catStructure = getCategoryStructure();
      return catStructure ? catStructure.has(categoryId) : false;
    },

    /**
     * Validates direct variant transition is safe for state machine.
     * CCP COMPLIANT: Validates state integrity, NOT business logic.
     *
     * Material-selector already validated compatibility (business logic layer).
     * This validates the IDs exist in our data structures (safety layer).
     *
     * @param {string} categoryId - Category ID
     * @param {string|number} variantId - Variant ID
     * @returns {boolean} True if state transition is safe
     */
    validateVariantForMaterials(categoryId, variantId) {
      // PATTERN 9: Validate category via conductor delegation
      const catStructure = getCategoryStructure();
      if (!catStructure || !catStructure.has(categoryId)) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid category for direct variant transition',
          new Error(`Category ${categoryId} not found in structure`),
          {
            method: 'validationManager.validateVariantForMaterials',
            categoryId,
            severity: 'warning',
            recoverable: true,
          }
        );
        return false;
      }

      // PATTERN 7: PPD-first variant validation (O(1))
      const ppd = getPPD();
      if (!ppd || !this._variantExistsInPPD(ppd, variantId)) {
        window.MurphyDoorConfigurator.main.handleError(
          'Invalid variant for direct variant transition',
          new Error(`Variant ${variantId} not found in product dictionary`),
          {
            method: 'validationManager.validateVariantForMaterials',
            variantId,
            severity: 'warning',
            recoverable: true,
          }
        );
        return false;
      }

      return true;
    },

    /**
     * Helper: Check if variant exists via PPD conductor delegation.
     * PPD-FIRST: Uses O(1) variantIdToProductIdMap instead of O(n*m) search.
     *
     * @private
     * @param {Object} ppd - Perfect Product Dictionary (from conductor delegation)
     * @param {string|number} variantId - Variant ID
     * @returns {boolean} True if variant exists
     */
    _variantExistsInPPD(ppd, variantId) {
      if (!ppd || !ppd.variantIdToProductIdMap) {
        isDev() &&
          console.error(
            '[ValidationManager] variantIdToProductIdMap not available in PPD'
          );
        return false;
      }

      // Dual-lookup: string (CompositeProduct synthetic) and numeric variant IDs
      // O(1) Map lookup instead of O(n*m) search
      const stringId = String(variantId);
      const numericVariantId = parseInt(variantId);

      return (
        ppd.variantIdToProductIdMap.has(stringId) ||
        ppd.variantIdToProductIdMap.has(numericVariantId)
      );
    },

    // ========================================================================
    // IMAGE GALLERY VALIDATION
    // ========================================================================

    /**
     * Validates image layer initialization data.
     *
     * @param {Object} data - Image layer init data
     * @returns {boolean} True if image layers can be initialized
     */
    validateImageLayerInit(data) {
      const { frontView, backView } = data;
      const DOM = getDOM();
      if (!DOM) return false;

      return (
        frontView instanceof HTMLElement &&
        backView instanceof HTMLElement &&
        DOM.validateViewElement(frontView, 'front') &&
        DOM.validateViewElement(backView, 'back') &&
        DOM.validateViewImage(frontView) &&
        DOM.validateViewImage(backView)
      );
    },

    /**
     * Validates image layer data structure.
     *
     * @param {Object} layerData - Layer data to validate
     * @returns {boolean} True if layer data is valid
     */
    validateImageLayerData(layerData) {
      return (
        layerData &&
        (layerData.frontLayer || layerData.backLayer) &&
        typeof layerData.isVisible === 'boolean' &&
        typeof layerData.zIndex === 'number'
      );
    },

    // ========================================================================
    // CART VALIDATION
    // ========================================================================

    /**
     * Validates a cart item structure.
     *
     * @param {Object} item - Cart item to validate
     * @returns {boolean} True if item is valid
     */
    validateCartItem(item) {
      return (
        item &&
        Number.isInteger(parseInt(item.id)) &&
        item.quantity > 0 &&
        item.properties &&
        this.validateItemProperties(item.properties)
      );
    },

    /**
     * Validates cart item properties.
     *
     * @param {Object} properties - Item properties
     * @returns {boolean} True if properties are valid
     */
    validateItemProperties(properties) {
      return (
        properties._Configuration_ID &&
        (properties._Base_Product !== undefined || properties._Category)
      );
    },

    /**
     * Validates a base product against the product structure.
     * PATTERN 9: Conductor delegation for product structure.
     *
     * @param {Object} item - Cart item
     * @returns {boolean} True if base product is valid
     */
    validateBaseProduct(item) {
      const prodStructure = getProductStructure();
      return prodStructure
        ? item.id === prodStructure.baseProduct.variant.id
        : false;
    },

    /**
     * Validates cart configuration completeness.
     * PATTERN 7: PPD-first for required categories lookup (O(1)).
     *
     * @param {Object} configuration - Cart configuration
     * @returns {boolean} True if all required categories have selections
     */
    validateCartConfiguration(configuration) {
      // PATTERN 7: PPD-first via conductor delegation
      const ppd = getPPD();
      if (!ppd || !ppd.categoryMetadata) return false;

      // O(1) read instead of O(n*m) nested iteration
      const requiredCategories =
        ppd.categoryMetadata.allRequiredCategoryIdsArray;
      const selectedCategories = Object.keys(configuration.selections);

      return requiredCategories.every((category) =>
        selectedCategories.includes(category)
      );
    },

    // ========================================================================
    // DISPLAY VALIDATION
    // ========================================================================

    /**
     * Validates price display value.
     *
     * @param {number} price - Price to validate
     * @returns {boolean} True if price is valid for display
     */
    validatePriceDisplay(price) {
      return typeof price === 'number' && !isNaN(price) && price >= 0;
    },

    /**
     * Validates shipping time range.
     *
     * @param {Object} time - Shipping time with low/high bounds
     * @returns {boolean} True if time range is valid
     */
    validateShippingTime(time) {
      return (
        time &&
        typeof time.low === 'number' &&
        typeof time.high === 'number' &&
        time.low >= 0 &&
        time.high >= time.low
      );
    },

    // ========================================================================
    // RECOVERY VALIDATION
    // ========================================================================

    /**
     * Validates recovered state.
     *
     * @param {Object} state - Recovered state to validate
     * @returns {boolean} True if recovered state is valid
     */
    validateRecoveredState(state) {
      // Material relationships are being refactored, skip material validation
      return true;
    },

    // ========================================================================
    // IMAGE & ELEMENT VALIDATION
    // ========================================================================

    /**
     * Validates image layer views.
     *
     * @param {Object} data - Image data with frontView and backView
     * @returns {boolean} True if image layers are valid
     */
    validateImageLayers(data) {
      const { frontView, backView } = data;
      const DOM = getDOM();
      if (!DOM) return false;

      return (
        frontView &&
        backView &&
        DOM.validateViewElement(frontView, 'front') &&
        DOM.validateViewElement(backView, 'back') &&
        DOM.validateViewImage(frontView) &&
        DOM.validateViewImage(backView)
      );
    },

    /**
     * Validates an element against a set of rules.
     *
     * @param {HTMLElement} element - Element to validate
     * @param {Array<Function>} rules - Validation rule functions
     * @returns {boolean} True if element passes all rules
     */
    validateElement(element, rules) {
      if (!element) return false;

      const DOM = getDOM();
      if (!DOM) return false;

      const value = DOM.getAttribute(element, 'data-value');
      if (!value) return false;

      return rules.every((rule) => rule(value));
    },

    /**
     * Validates a selection for a given category.
     *
     * @param {string} categoryId - Category ID
     * @param {*} selection - Selection to validate
     * @returns {boolean} True if selection is valid in category
     */
    validateSelection(categoryId, selection) {
      const DOM = getDOM();
      if (!DOM) return false;

      const categoryElement = DOM.getCategoryElement(categoryId);
      if (!categoryElement) return false;

      const selectionElement = DOM.getSelectionElement(selection);
      return Boolean(selectionElement);
    },

    // ========================================================================
    // PATTERN 15: CLEANUP (10 points)
    // PATTERN 20: CONDUCTOR SINGLE PUBLICATION (10 points)
    // ========================================================================

    /**
     * Cleans up all event subscriptions and resets module state.
     * Called during system shutdown for proper resource management.
     */
    cleanup() {
      isDev() && console.log('[ValidationManager] Cleaning up resources');

      cleanupHandlers.forEach((unsubscribe) => unsubscribe());
      cleanupHandlers.length = 0;

      isInitialized = false;
      bootstrapComplete = false;

      publishEvent(EVENTS.MODULE_VALIDATION_MANAGER_CLEANUP, {
        module: 'validationManager',
        timestamp: Date.now(),
      });
    },
  };
})(window);
